import React, { useState, useEffect } from 'react';
import { ActivityLog } from '../../types/index.js';
import { api } from '../../services/api.js';
import {
  Search,
  Filter,
  Shield,
  ShieldAlert,
  History,
  RefreshCw,
  UserCheck,
  CreditCard,
  Settings,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Download,
  Terminal,
} from 'lucide-react';
import { exportToExcel } from '../../utils/reportExport.js';

export const AuditLogBrowser: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedActionType, setSelectedActionType] = useState<string>('ALL');
  const [selectedDivisi, setSelectedDivisi] = useState<string>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await api.getActivityLogs({
        actionType: selectedActionType,
        divisi: selectedDivisi,
        status: selectedStatus,
        search,
      });
      setLogs(data);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [selectedActionType, selectedDivisi, selectedStatus]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLogs();
  };

  const handleExportCsv = () => {
    exportToExcel({
      title: 'Audit Log Keamanan & Aktivitas Sistem',
      subtitle: 'Rekam Jejak Transaksi dan Modifikasi Hak Akses Pengguna',
      filterInfo: `Filter Action: ${selectedActionType} | Status: ${selectedStatus} | Divisi: ${selectedDivisi}`,
      columns: [
        { header: 'No', key: '_index', align: 'center' },
        { header: 'Waktu & Tanggal', key: 'timestamp' },
        { header: 'User Akun', key: 'username' },
        { header: 'Tipe Audit', key: 'actionType' },
        { header: 'Divisi / Modul', key: 'divisi' },
        { header: 'Aktivitas', key: 'activity' },
        { header: 'Deskripsi Rincian', key: 'detail' },
        { header: 'IP Address', key: 'ipAddress' },
        { header: 'Status', key: 'status', align: 'center' },
      ],
      data: logs.map((l) => ({
        ...l,
        actionType: l.actionType || 'TRANSACTION',
        status: l.status || 'SUCCESS',
      })),
      summaryRows: [{ label: 'Total Log Terekam', value: logs.length }],
    });
  };

  // Metrics for Security Auditing Header
  const totalTrxLogs = logs.filter((l) => l.actionType === 'TRANSACTION').length;
  const totalUserMods = logs.filter((l) => l.actionType === 'USER_MODIFICATION').length;
  const totalSecurityEvents = logs.filter((l) => l.actionType === 'SECURITY').length;
  const totalWarnings = logs.filter((l) => l.status === 'WARNING' || l.status === 'FAILED').length;

  return (
    <div className="space-y-4">
      {/* Top Security Audit Metrics Banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center text-sky-700">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Log Transaksi
            </span>
            <span className="text-lg font-mono font-bold text-slate-900">{totalTrxLogs}</span>
          </div>
        </div>

        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-700">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Modifikasi Akun
            </span>
            <span className="text-lg font-mono font-bold text-slate-900">{totalUserMods}</span>
          </div>
        </div>

        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-700">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Autentikasi & Auth
            </span>
            <span className="text-lg font-mono font-bold text-slate-900">{totalSecurityEvents}</span>
          </div>
        </div>

        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-xs flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center text-rose-700">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Void / Warning
            </span>
            <span className="text-lg font-mono font-bold text-rose-600">{totalWarnings}</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-sky-700" />
            <span className="text-sm font-bold text-slate-900">
              Audit Trail & Database Activity Logs (`audit_log`)
            </span>
            <span className="bg-slate-100 text-slate-600 text-[10px] font-mono font-semibold px-2 py-0.5 rounded">
              {logs.length} Log Records
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleExportCsv}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold transition shadow-xs cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari kata kunci, username, detail transaksi, NIS..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-lg focus:outline-hidden focus:ring-1 focus:ring-sky-500 font-medium"
            />
          </div>

          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-semibold text-slate-600">Tipe Aksi:</span>
            <select
              value={selectedActionType}
              onChange={(e) => setSelectedActionType(e.target.value)}
              className="px-2.5 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-medium"
            >
              <option value="ALL">Semua Tipe</option>
              <option value="TRANSACTION">Transaksi Keuangan</option>
              <option value="USER_MODIFICATION">Modifikasi Pengguna</option>
              <option value="SECURITY">Keamanan & Auth</option>
              <option value="SYSTEM">System & Webhook</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-semibold text-slate-600">Status:</span>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-2.5 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs font-medium"
            >
              <option value="ALL">Semua Status</option>
              <option value="SUCCESS">SUCCESS (Normal)</option>
              <option value="WARNING">WARNING (Void / Warning)</option>
              <option value="FAILED">FAILED (Gagal)</option>
            </select>
          </div>

          <button
            type="submit"
            className="px-3 py-1.5 bg-sky-700 hover:bg-sky-600 text-white rounded-lg text-xs font-semibold transition cursor-pointer"
          >
            Filter
          </button>
        </form>
      </div>

      {/* Main Audit Log Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
              <tr>
                <th className="py-2.5 px-3 w-12 text-center">No</th>
                <th className="py-2.5 px-3">Waktu & Tanggal</th>
                <th className="py-2.5 px-3">User Pelaksana</th>
                <th className="py-2.5 px-3">Kategori</th>
                <th className="py-2.5 px-3">Aktivitas</th>
                <th className="py-2.5 px-4">Deskripsi Rincian</th>
                <th className="py-2.5 px-3 font-mono">IP Address</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-center">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-slate-400">
                    Tidak ada catatan audit yang sesuai dengan kriteria filter.
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => {
                  const isTrx = log.actionType === 'TRANSACTION';
                  const isUserMod = log.actionType === 'USER_MODIFICATION';
                  const isSecurity = log.actionType === 'SECURITY';

                  return (
                    <tr
                      key={log.id || idx}
                      className="hover:bg-sky-50/40 transition cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="py-2.5 px-3 text-center font-mono text-slate-400">{idx + 1}</td>
                      <td className="py-2.5 px-3 text-slate-600 whitespace-nowrap font-mono text-[11px]">
                        {log.timestamp}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="font-bold text-slate-900 block">{log.username}</span>
                        <span className="text-[10px] text-slate-400">{log.divisi}</span>
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                            isUserMod
                              ? 'bg-amber-100 text-amber-800'
                              : isSecurity
                              ? 'bg-indigo-100 text-indigo-800'
                              : isTrx
                              ? 'bg-sky-100 text-sky-800'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {isUserMod && <UserCheck className="w-3 h-3" />}
                          {isSecurity && <Shield className="w-3 h-3" />}
                          {isTrx && <CreditCard className="w-3 h-3" />}
                          <span>{log.actionType || 'TRANSACTION'}</span>
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-slate-800">{log.activity}</td>
                      <td className="py-2.5 px-4 text-slate-600 max-w-md truncate" title={log.detail}>
                        {log.detail}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-slate-500 text-[11px] whitespace-nowrap">
                        {log.ipAddress || '127.0.0.1'}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            log.status === 'WARNING'
                              ? 'bg-rose-100 text-rose-800'
                              : log.status === 'FAILED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {log.status || 'SUCCESS'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLog(log);
                          }}
                          className="px-2 py-1 bg-slate-100 hover:bg-sky-100 text-slate-700 hover:text-sky-700 rounded text-[11px] font-semibold transition"
                        >
                          Lihat
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-sky-700" />
                <h3 className="font-bold text-sm text-slate-900">Detail Audit Security Log</h3>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-slate-400 hover:text-slate-600 text-sm font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-semibold">ID Audit:</span>
                <span className="col-span-2 font-mono text-slate-900 font-bold">{selectedLog.id}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-semibold">Waktu Tercatat:</span>
                <span className="col-span-2 font-mono text-slate-800">{selectedLog.timestamp}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-semibold">Pengguna (User):</span>
                <span className="col-span-2 font-bold text-slate-900">
                  {selectedLog.username} ({selectedLog.divisi})
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-semibold">Kategori Audit:</span>
                <span className="col-span-2 font-mono font-bold text-sky-800">
                  {selectedLog.actionType || 'TRANSACTION'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-semibold">Aktivitas:</span>
                <span className="col-span-2 font-semibold text-slate-900">{selectedLog.activity}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-semibold">IP Address:</span>
                <span className="col-span-2 font-mono text-slate-700">{selectedLog.ipAddress}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-semibold">Status:</span>
                <span className="col-span-2">
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      selectedLog.status === 'WARNING'
                        ? 'bg-rose-100 text-rose-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    {selectedLog.status || 'SUCCESS'}
                  </span>
                </span>
              </div>
              {selectedLog.targetId && (
                <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-slate-100">
                  <span className="text-slate-500 font-semibold">Target Entity / ID:</span>
                  <span className="col-span-2 font-mono text-slate-800">
                    {selectedLog.targetEntity ? `${selectedLog.targetEntity} : ` : ''}
                    {selectedLog.targetId}
                  </span>
                </div>
              )}
              <div className="pt-2">
                <span className="text-slate-500 font-semibold block mb-1">Rincian Lengkap:</span>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-800 font-mono text-[11px] leading-relaxed break-words whitespace-pre-wrap">
                  {selectedLog.detail}
                </div>
              </div>
            </div>

            <div className="pt-2 text-right">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold transition cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
